import React, { useState, useEffect, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import AppBar from './components/AppBar/AppBar';
import MarketWatch from './components/MarketWatch/MarketWatch';
import Orders from './components/Orders/Orders';
import Portfolio from './components/Portfolio';
import { fetchPositionsAction } from './store/actions/portfolio';
import Funds from './components/Funds/Funds';
import Charts from './components/Charts/Charts';
import usePositionUpdates from './hooks/wsUpdates/usePositonUpdates';
import DraggableDivider from './components/DraggableDivider';
import Login from './components/Login/Login';
import TwoFactorAuth from './components/TwoFactorAuth/TwoFactorAuth';
import Profile from './components/Profile/Profile';
import { NotificationProvider } from './context/NotificationContext';
import { ContractProvider } from './context/ContractContext';
import { useDispatch} from 'react-redux';
import useOrderUpdates from './hooks/wsUpdates/useOrderUpdates';
import octopusInstance from './services/octopus/octopusInstance';
import { fetchUserProfile, fetchUserSettings } from './services/backendAPI';
import { fetchMarketWatchesAction, } from './store/actions/watchlist';
import useMediaQuery from '@mui/material/useMediaQuery';
import BottomNav from './components/Navigation/BottomNav';
import Overview from './components/Overview/Overview';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { setProfileDataAction, setUserSettingsAction } from './store/actions/profile';
import OrderWindowHOC from './components/OrderWindow/OrderWindowHOC';
import PayIn from './components/PayIn/PayIn';
import TradeHistory from './components/TradeHistory/TradeHistory';
import TradeHistoryDetails from './components/TradeHistory/TradeHistoryDetails';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useFundsQuery from './hooks/useFundsQuery';
import { fetchFundsView } from './store/actions/funds';
import FundsProvider from './components/Common/FundsProvider';
import useFundsUpdates from './hooks/wsUpdates/useFundsUpdates';
import PnlHistoryDetails from './components/TradeHistory/PnlHistoryDetails';
import Ledger from './components/TradeHistory/Ledger';
import LedgerDetails from './components/TradeHistory/LedgerDetails';
import { refreshFundsAction } from './store/actions/funds';
import ContactUs from './components/ContactUs';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 'calc(100vh - 64px)',
  marginTop: '64px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    height: 'calc(100vh - 112px)',
    marginTop: '56px',
    marginBottom: '56px',
    overflow: 'hidden',
  },
}));

const ContentPane = styled(Box)(({ width, theme }) => ({
  width: `${width}%`,
  overflow: 'auto',
  transition: 'width 0.1s ease',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: 'calc(100vh - 56px)',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
}));

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // usePositions(); // This is causing refreshes - remove it

  // console.log("Total",total);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });

  const [is2FAVerified, setIs2FAVerified] = useState(() => {
    return localStorage.getItem('2faVerified') === 'true';
  });
  // const [isValidToken, setIsValidToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [activeComponent, setActiveComponent] = useState('profile');
  const [previousComponent, setPreviousComponent] = useState(null);

  const enhancedSetActiveComponent = useCallback((newComponent) => {
    navigate(`/${newComponent}`);
    setPreviousComponent(activeComponent);
    setActiveComponent(newComponent);
  }, [activeComponent, navigate]);




  const [leftPaneWidth, setLeftPaneWidth] = useState(() => {
    const storedWidth = localStorage.getItem('leftPaneWidth');
    return storedWidth ? parseFloat(storedWidth) : 75;
  });

  const [chartSymbol, setChartSymbol] = useState(null);
  const [chartKey, setChartKey] = useState(0);

  const dispatch = useDispatch();

  let token = window.localStorage.getItem("auth_token");

  useEffect(() => {
  
    const initializeConnection = async () => {
      if (token && isAuthenticated && is2FAVerified) {
        try {
          const { data } = await fetchUserProfile();
          const { data: settings } = await fetchUserSettings();
          data.shortName = data.name.match(/\b(\w)/g).join("");
          localStorage.setItem("user_type", data.user_type);
          dispatch(setProfileDataAction(data));

          if (settings) {
            const processedSettings = {
              ...settings,
              client_id: data.client_id || settings.client_id
            };
            dispatch(setUserSettingsAction(processedSettings));
          }

          // Connect to WebSocket if tab is visible
          if (document.visibilityState === 'visible') {
            try {
              await octopusInstance.connect();
              console.log('WebSocket connected successfully');
            } catch (err) {
              console.error('Initial WebSocket connection error:', err);
            }
          }

          dispatch(fetchPositionsAction("historical"));
          dispatch(fetchMarketWatchesAction(true));
        } catch (error) {
          console.error('Connection error:', error);
          localStorage.clear();
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initializeConnection();

    return () => {
      localStorage.removeItem("user_type");
    };
  }, [token, isAuthenticated, is2FAVerified, dispatch]);


  const connect = async () => {
    try {
      // Use octopusInstance
      await octopusInstance.connect();
      return true;
    } catch (error) {
      console.error('WebSocket connection error:', error);
      throw error;
    }
  }

  useEffect(() => {
    if (isAuthenticated && is2FAVerified) {
      // Connect to WebSocket with error handling
      const setupConnection = async () => {
        try {
          await connect();
          console.log('WebSocket connection established successfully');
          
          // Fetch data after connection is established
          dispatch(fetchMarketWatchesAction(true));
          dispatch(fetchFundsView());
        } catch (error) {
          console.error('Failed to connect to WebSocket:', error);
        }
      };
      
      setupConnection();
    }
  }, [isAuthenticated, is2FAVerified]);

  useOrderUpdates();
  useFundsUpdates();
  usePositionUpdates();

  useEffect(() => {
    localStorage.setItem('leftPaneWidth', leftPaneWidth.toString());
    // localStorage.setItem('token', auth_token);
  }, [leftPaneWidth]);

  const handleDrag = (dragDiff) => {
    const containerWidth = document.querySelector('.MuiBox-root').offsetWidth;
    const newWidth = leftPaneWidth - (dragDiff / containerWidth * 100);
    if (newWidth >= 65 && newWidth <= 85) {
      setLeftPaneWidth(newWidth);
    }
  };

  const handleChartOpen = (symbol) => {
    setChartKey(prevKey => prevKey + 1);
    setChartSymbol(symbol);
    setActiveComponent('charts');
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handle2FAVerify = () => {
    setIs2FAVerified(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('2faVerified');
    setIsAuthenticated(false);
    setIs2FAVerified(false);
  };

  // Component to handle invalid /overview route
  const InvalidOverviewHandler = () => {
    useEffect(() => {
      console.log('⚠️ Invalid /overview route accessed - logging out user');
      handleLogout();
      // Redirect to login after logout (login will then redirect to appropriate default)
      navigate('/login');
    }, []);
    
    return null; // Return nothing while logout happens
  };

  const renderComponent = useCallback(() => {
    switch (activeComponent) {
      case 'dashboard':
        return <Profile />;
      case 'marketwatch':
        return <MarketWatch onChartOpen={handleChartOpen} />;
      case 'orders':
        return <Orders />;
      case 'portfolio':
        return <Portfolio />;
      case 'funds':
        return <Funds />;
      case 'charts':
        return chartSymbol ? <Charts key={chartKey} symbol={chartSymbol} /> : null;
      case 'profile':
        return <Profile />;
      case 'overview':
        return <Overview />;
      case 'tradehistory':
        return <TradeHistory />;
      case 'support':
        return <ContactUs />;
      default:
        return <Profile />;
    }
  }, [activeComponent, chartSymbol, chartKey, location.pathname]);

  useEffect(() => {
    // Map of routes to components for activeComponent state
    const routeToComponent = {
      '/overview/tdv': 'overview',
      '/marketwatch': 'marketwatch',
      '/orders': 'orders',
      '/portfolio': 'portfolio',
      '/funds': 'funds',
      '/profile': 'profile',
      '/dashboard': 'dashboard',
      '/tradehistory': 'tradehistory'
    };

    // Find matching route and set active component
    const matchedComponent = Object.entries(routeToComponent)
      .find(([route]) => location.pathname.startsWith(route));

    if (matchedComponent) {
      setActiveComponent(matchedComponent[1]);
    } else if (location.pathname.startsWith('/overview')) {
      setActiveComponent('overview');
    } else {
      // For any other routes, default based on device type
      setActiveComponent(isMobile ? 'marketwatch' : 'dashboard');
    }
  }, [location.pathname, isMobile]);

  // Add a function to monitor the connection status
  useEffect(() => {
    if (isAuthenticated && is2FAVerified) {
      let reconnectTimeout = null;
      let lastReconnectAttempt = 0;
      const RECONNECT_COOLDOWN = 5000; // 5 seconds cooldown between reconnect attempts

      // Only attempt reconnection if not already trying and cooldown period has passed
      const attemptReconnectIfNeeded = () => {
        const now = Date.now();
        if (now - lastReconnectAttempt < RECONNECT_COOLDOWN) {
          console.log('Reconnection attempt too soon, skipping');
          return;
        }

        // Only reconnect if not connected
        if (!octopusInstance.isConnected()) {
          console.log('WebSocket disconnected, attempting to reconnect...');
          lastReconnectAttempt = now;
          
          octopusInstance.connect()
            .then(() => {
              console.log('Reconnection successful, refreshing market and funds data');
              // After successful reconnection, reload watchlists and funds to refresh data
              dispatch(fetchMarketWatchesAction(true));
              dispatch(refreshFundsAction(true));
            })
            .catch(error => {
              console.error('Reconnection failed:', error);
            });
        } else {
          // Connection is active, but don't refresh data on every check
          // Only log that connection is active
          console.log('Connection active - maintaining WebSocket connection');
        }
      };
      
      // Handle visibility change
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('Tab became visible, scheduling connection check');
          
          // Clear any existing timeout
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
          }
          
          // Schedule a single reconnect attempt after a short delay
          // This prevents multiple rapid attempts if visibility changes quickly
          reconnectTimeout = setTimeout(() => {
            attemptReconnectIfNeeded();
            reconnectTimeout = null;
          }, 1000);
        }
      };
      
      // Set up periodic connection check - less frequent
      const connectionCheckInterval = setInterval(attemptReconnectIfNeeded, 30000);
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
        clearInterval(connectionCheckInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [isAuthenticated, is2FAVerified, dispatch]);

  if (!isAuthenticated) {
    return (
      <>
        <NotificationProvider>
          <ContractProvider>
            <FundsProvider>
              <CssBaseline />
              <Routes>
                <Route path="/login" element={<Login onLogin={handleLogin} onVerify={handle2FAVerify} />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </FundsProvider>
          </ContractProvider>
        </NotificationProvider>
      </>
    );
  }

  if (!is2FAVerified) {
    return (
      <>
        <NotificationProvider>
          <ContractProvider>
            <FundsProvider>
              <CssBaseline />
              <Routes>
                <Route path="*" element={<TwoFactorAuth onVerify={handle2FAVerify} />} />
              </Routes>
            </FundsProvider>
          </ContractProvider>
        </NotificationProvider>
      </>
    );
  }

  return (
    <>
      <NotificationProvider>
        <ContractProvider>
          <FundsProvider>
            <CssBaseline />
            <AppBar
              onLogout={handleLogout}
              activeComponent={activeComponent}
              onNavigate={enhancedSetActiveComponent}
              isMobile={isMobile}
            />
            {isMobile ? (
              <BottomNav
                activeComponent={activeComponent}
                onNavigate={enhancedSetActiveComponent}
              />
            ) : null}
            <Container>
              {!isMobile && activeComponent !== 'marketwatch' && (
                <>
                  <ContentPane width={100 - leftPaneWidth}>
                    <MarketWatch onChartOpen={handleChartOpen} />
                  </ContentPane>
                  <DraggableDivider onDrag={handleDrag} />
                </>
              )}
              <ContentPane
                width={isMobile || activeComponent === 'marketwatch' ? 100 : leftPaneWidth}
                sx={{
                  overflow: "hidden",
                  p: 2,
                  [`${theme.breakpoints.down('md')}`]: {
                    p: 1,
                  }
                }}
              >
                <Box sx={{
                  height: '100%',
                  overflow: 'hidden',
                }}>
                  <Routes>
                    <Route path="/overview/tdv/:exchange/:token" element={<Overview />} />
                    <Route 
                      path="/overview" 
                      element={<InvalidOverviewHandler />} 
                    />
                    <Route 
                      path="/" 
                      element={
                        <Navigate 
                          to={isMobile ? "/marketwatch" : "/dashboard"} 
                          replace 
                        />
                      } 
                    />
                    <Route path="/marketwatch" element={renderComponent()} />
                    <Route path="/orders" element={renderComponent()} />
                    <Route path="/portfolio" element={renderComponent()} />
                    <Route path="/funds" element={renderComponent()} />
                    <Route path="/profile" element={renderComponent()} />
                    <Route path="/charts" element={renderComponent()} />
                    <Route path="/dashboard" element={renderComponent()} />
                    <Route path="/tradehistory" element={renderComponent()} />
                    <Route path="/support" element={<ContactUs />} />
                    <Route path="/trade-history" element={<TradeHistory />} />
                    <Route path="/trade-history/details" element={<TradeHistoryDetails />} />
                    <Route path="/pnl-history/details" element={<PnlHistoryDetails />} />
                    <Route path="/ledger" element={<Ledger />} />
                    <Route path="/ledger/details" element={<LedgerDetails />} />
                    <Route 
                      path="*" 
                      element={
                        <Navigate 
                          to={isMobile ? "/marketwatch" : "/dashboard"} 
                          replace 
                        />
                      } 
                    />
                  </Routes>
                </Box>
              </ContentPane>
            </Container>
            <OrderWindowHOC />
          </FundsProvider>
        </ContractProvider>
      </NotificationProvider>
    </>
  );
};

export default App; 